import type { ReactNode } from 'react';
import type { AuthFeedback, AuthFeedbackTone } from './types';

type ErrorMessageProps = {
    message?: ReactNode;
    id?: string;
    className?: string;
    errorType?: string;
};

type FeedbackMessageProps = {
    feedback?: AuthFeedback | null;
    id?: string;
    tone?: AuthFeedbackTone;
    className?: string;
};

const feedbackToneClasses: Record<AuthFeedbackTone, string> = {
    error: 'mt-2.5 p-3 text-sm font-extrabold text-paprika bg-lychee/50 rounded-lg',
    info: 'p-3 bg-blueberry/10 text-blackberry text-sm rounded-lg',
    success: 'p-3 bg-avocado/10 text-matcha text-sm rounded-lg',
};

function joinClasses(...classes: Array<string | false | null | undefined>) {
    return classes.filter(Boolean).join(' ');
}

function hasMessage(message?: ReactNode) {
    if (message === undefined || message === null || message === false) {
        return false;
    }

    if (typeof message === 'string' && message.trim().length === 0) {
        return false;
    }

    return true;
}

export function ErrorMessage({ message, id, className, errorType }: ErrorMessageProps) {
    if (!hasMessage(message)) return null;

    return (
        <p
            id={id}
            role="alert"
            data-error-type={errorType}
            className={joinClasses(feedbackToneClasses.error, className)}
        >
            {message}
        </p>
    );
}

export function FeedbackMessage({ feedback, id, tone = 'info', className }: FeedbackMessageProps) {
    if (!feedback) return null;

    const resolvedTone = feedback.type ?? tone;
    const message =
        resolvedTone === 'error' ? (
            <ErrorMessage
                id={id}
                message={feedback.message}
                errorType={feedback.errorType}
                className={className}
            />
        ) : (
            <p
                id={id}
                data-feedback-type={resolvedTone}
                className={joinClasses(feedbackToneClasses[resolvedTone], className)}
            >
                {feedback.message}
            </p>
        );

    return message;
}
